/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Box, Flex, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { calculateSFPCells } from '../../../../utils/ews-sfp-utils';
import { checkSelectedActiveColumn } from '../../../../utils/market-development';
import { getYear } from '../../utils';
import Spreadsheet from 'react-spreadsheet';
import EWSLabels from '../ews-template-labels';
import Loading from '../../../../components/Loading';
import { isNullOrBlank } from '../../../../utils/checker';
import { toFormatCurrency } from '../../../../utils/format';
import { getCompanyTypesByFilterOptionValue, getDefaultFilterOptionValue } from '../../../../utils/ews';

const Q_REPORT = gql`
	query getQuarterlyEarlyWarningSFPSummaryReport(
		$year: Float!
		$companyTypes: [InsuranceInstitutionsType!]
		$companyId: String
		$quarters: [EarlyWarningTypeStatement!]
	) {
		getQuarterlyEarlyWarningSFPSummaryReport(year: $year, companyTypes: $companyTypes, companyId: $companyId, quarters: $quarters)
	}
`;

export default function SFPSpreadSheetTable({
	filterOptions = [],
	quarters = null,
	year = null,
	companyType = null,
	companyId = null,
	onReadyForExport = () => null,
}) {
	const toast = useToast();
	const { t } = useTranslation();
	const [active, setActive] = useState(null);
	const [detail, setDetail] = useState([]);
	const [q1Cells, setQ1Cells] = useState([]);
	const [q2Cells, setQ2Cells] = useState([]);
	const [q3Cells, setQ3Cells] = useState([]);
	const [q4Cells, setQ4Cells] = useState([]);
	const [totalCells, setTotalCells] = useState([]);
	const [loadReport, { loading, error, data }] = useLazyQuery(Q_REPORT);

	useEffect(load, [year, companyType, companyId, quarters, filterOptions]);

	useEffect(() => data?.getQuarterlyEarlyWarningSFPSummaryReport && setDetail(data?.getQuarterlyEarlyWarningSFPSummaryReport), [data]);

	useEffect(() => detail && quarterDetail(detail), [detail]);

	useEffect(() => {
		if (error) {
			if (error?.graphQLErrors?.length > 0) {
				error?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(error?.message ? error?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [error]);

	function load() {
		const variables = { year: getYear(year), companyTypes: getCompanyTypes(companyType), companyId, quarters: quarters ? JSON.parse(quarters) : null };
		loadReport({ variables });
	}

	function getCompanyTypes(value) {
		return getCompanyTypesByFilterOptionValue(value ? value : getDefaultFilterOptionValue({ filterOptions }));
	}

	function quarterDetail() {
		const localQ1Cells = quarterCells({ code: 'SFP', labels: detail?.labels, quarter: detail?.q1 });
		const localQ2Cells = quarterCells({ code: 'SFP', labels: detail?.labels, quarter: detail?.q2 });
		const localQ3Cells = quarterCells({ code: 'SFP', labels: detail?.labels, quarter: detail?.q3 });
		const localQ4Cells = quarterCells({ code: 'SFP', labels: detail?.labels, quarter: detail?.q4 });
		const localTotalCells = quarterCells({ code: 'SFP', labels: detail?.labels, quarter: detail?.total });
		setQ1Cells(localQ1Cells);
		setQ2Cells(localQ2Cells);
		setQ3Cells(localQ3Cells);
		setQ4Cells(localQ4Cells);
		setTotalCells(localTotalCells);

		onReadyForExport({
			labels: detail?.labels,
			q1Cells: localQ1Cells,
			q2Cells: localQ2Cells,
			q3Cells: localQ3Cells,
			q4Cells: localQ4Cells,
			totalCells: localTotalCells,
		});
	}

	function quarterCells({ code = null, labels = [], quarter = null }) {
		if (quarter) {
			const localCells = labels?.map((label) => {
				const values = [
					{ value: 0, readOnly: true },
					{ value: 0, readOnly: true },
				];
				const labelObject = quarter?.find((e) => e?.code === label?.code);
				return labelObject?.values || values;
			});
			const { cells } = calculateSFPCells({
				code,
				cells: localCells,
				numLabel: labels?.length,
				readOnly: true,
			});
			const truncatedCells = cells.map((row) => {
				return row.map((cell, colIndex) => {
					return {
						...cell,
						value: (() => {
							if (false === isNullOrBlank(cell?.value)) {
								if (colIndex === 0) {
									return toFormatCurrency(cell?.value);
								} else {
									return toFormatCurrency(cell?.value, null);
								}
							}
							return cell?.value;
						})(),
					};
				});
			});
			return truncatedCells;
		}
		return null;
	}

	return (
		<>
			<Box>
				{loading && (
					<>
						<Loading />
					</>
				)}
				{!loading && !detail && (
					<>
						<Text color="gray.400" textAlign="center" mt={4}>
							{t('No Data')}
						</Text>
					</>
				)}
				{!loading && detail && (
					<>
						<Box className="ews-container">
							<div className="ews-2-row-head">{t('descriptionReport')}</div>
							<Flex>
								{q1Cells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver" style={{ color: 'var(--chakra-colors-blue-600)' }}>
													{t('q1')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td
													className={checkSelectedActiveColumn(active, 0 + 0) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t('USD')}
												</td>
												<td
													className={checkSelectedActiveColumn(active, 1 + 0) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
								{q2Cells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver" style={{ color: 'var(--chakra-colors-blue-600)' }}>
													{t('q2')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td
													className={checkSelectedActiveColumn(active, 0 + 5) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t('USD')}
												</td>
												<td
													className={checkSelectedActiveColumn(active, 1 + 5) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
								{q3Cells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver" style={{ color: 'var(--chakra-colors-blue-600)' }}>
													{t('q3')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td
													className={checkSelectedActiveColumn(active, 0 + 10) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t('USD')}
												</td>
												<td
													className={checkSelectedActiveColumn(active, 1 + 10) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
								{q4Cells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver" style={{ color: 'var(--chakra-colors-blue-600)' }}>
													{t('q4')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td
													className={checkSelectedActiveColumn(active, 0 + 15) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t('USD')}
												</td>
												<td
													className={checkSelectedActiveColumn(active, 1 + 15) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
								{totalCells?.length > 0 && (
									<table className="spreadsheet_custom_header">
										<thead>
											<tr style={{ height: '34px' }}>
												<td colSpan="2" className="ews-background-light-silver" style={{ color: 'var(--chakra-colors-blue-600)' }}>
													{t('Total')}
												</td>
											</tr>
											<tr style={{ height: '34px' }}>
												<td
													className={checkSelectedActiveColumn(active, 0 + 20) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t('USD')}
												</td>
												<td
													className={checkSelectedActiveColumn(active, 1 + 20) ? 'active-column' : 'ews-background-light-silver'}
													style={{ color: 'var(--chakra-colors-blue-600)' }}
												>
													{t(`KHR'000`)}
												</td>
											</tr>
										</thead>
									</table>
								)}
							</Flex>
							<div>
								<EWSLabels type="EWS" labels={detail?.labels} active={active} blueDesc={true} />
								<Flex>
									{q1Cells?.length > 0 && (
										<Spreadsheet
											data={q1Cells}
											onChange={(cells) => {
												setQ1Cells(
													q1Cells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, []),
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 0, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
									{q2Cells?.length > 0 && (
										<Spreadsheet
											data={q2Cells}
											onChange={(cells) => {
												setQ2Cells(
													q2Cells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, []),
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 5, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
									{q3Cells?.length > 0 && (
										<Spreadsheet
											data={q3Cells}
											onChange={(cells) => {
												setQ3Cells(
													q3Cells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, []),
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 10, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
									{q4Cells?.length > 0 && (
										<Spreadsheet
											data={q4Cells}
											onChange={(cells) => {
												setQ4Cells(
													q4Cells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, []),
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 15, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
									{totalCells?.length > 0 && (
										<Spreadsheet
											data={totalCells}
											onChange={(cells) => {
												setQ4Cells(
													totalCells?.reduce((pre, cur, i) => {
														let localPre = pre;
														localPre.push([
															{ value: cur[0]?.value, readOnly: true },
															{ value: cur[1]?.value, readOnly: true },
														]);
														return localPre;
													}, []),
												);
											}}
											onActivate={(e) => setActive({ ...e, column: e?.column + 20, t: 'EWS' })}
											hideRowIndicators
											hideColumnIndicators
										/>
									)}
								</Flex>
							</div>
						</Box>
					</>
				)}
			</Box>
		</>
	);
}
